import Fuse from "fuse.js";
import { v4 as uuidv4 } from "uuid";

import { normalizeIdentifiers } from "@/configuration/mrn-primary";
import { PatientMatchMethod } from "@/shared";
import { createOrUpdateAppointmentFromEhr } from "@/src/lib/comms/scheduling/appointment";
import type { PatientDetails } from "@/src/lib/ehr/patient-details";
import { logAndThrow, logger } from "@/src/lib/logging/logger";
import { stats } from "@/src/lib/logging/stats.server";
import { mergeNonNull } from "@/src/lib/merge-non-null";
import { withDurationMetric } from "@/src/lib/metrics";
import { FuzzyNameMatchThreshold, isExactNameMatch, matchByName, normalizeFullName } from "@/src/lib/name-match";
import { mergeEhrIdentifiers } from "@/src/lib/resolve-ehr-ids";
import { dobMatch, hasConflictingIdentifyingFields } from "@/src/lib/similar";
import { formatUsDateToYyyyMmDd } from "@/src/lib/utils/date-and-phone-utils";
import { hasValue } from "@/src/lib/utils/object-utils";
import { type Coverage } from "@/src/models/coverage";
import { Group } from "@/src/models/group";
import { Patient, PatientStatus, PatientType } from "@/src/models/patient";
import { stripPersonDemographicPlaceholders } from "@/src/models/person";
import { insertCoverage } from "@/src/repo/coverage";
import {
  getAllPatientIds as getAllPatientIdsRepo,
  getPatient,
  getPatientByDisplayId as getPatientByDisplayIdRepo,
  getPatientByEhrId as getPatientByEhrIdRepo,
  getPatientsByPhoneNumber as getPatientsByPhoneNumberRepo,
  getPatientBySecondaryEhrId as getPatientBySecondaryEhrIdRepo,
  getPatientsByIds as getPatientsByIdsRepo,
  handlePatientRelations,
  insertPatient,
  findPatients as findPatientsRepo,
  searchPatients as searchPatientsRepo,
  updatePatient,
  type PatientLookupParams,
  type PatientSearchParams,
} from "@/src/repo/patient";
import { patientAlertRepository } from "@/src/repo/patient-alert";

// ---------------------------------------------------------------------------
// Patient search (DB search)
// ---------------------------------------------------------------------------

export async function findPatients(params: PatientLookupParams, group: Group): Promise<Patient[]> {
  return findPatientsRepo(params, group);
}

export async function searchPatients(params: PatientSearchParams, group: Group): Promise<Patient[]> {
  return searchPatientsRepo(params, group);
}

export async function getAllPatientIds(group: Group, type?: PatientType) {
  return getAllPatientIdsRepo(group, type);
}

// ---------------------------------------------------------------------------
// Patient lookup (Single record / ID based)
// ---------------------------------------------------------------------------

export async function getPatientById(id: string, group: Group) {
  return getPatient(id, group);
}

export async function getPatientByDisplayId(displayId: string, group: Group) {
  return getPatientByDisplayIdRepo(displayId, group);
}

export async function getPatientByEhrId(ehrId: string, groupId: string) {
  return getPatientByEhrIdRepo(ehrId, groupId);
}

export async function getPatientBySecondaryEhrId(secondaryEhrId: string, groupId: string) {
  return getPatientBySecondaryEhrIdRepo(secondaryEhrId, groupId);
}

export async function getPatientsByPhoneNumber(params: { phoneNumber: string; groupId: string }) {
  return getPatientsByPhoneNumberRepo(params);
}

export async function getPatientsByIds(ids: string[], group: Group) {
  return getPatientsByIdsRepo(ids, group);
}

// ---------------------------------------------------------------------------
// Patient search (UI / client-side filtering)
// ---------------------------------------------------------------------------

export function patientsSearch(patients: Patient[], query: Partial<Patient> | string | null): Patient[] {
  if (!patients || patients.length === 0 || query === null) {
    return [];
  }

  if (typeof query === "string") {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return [];
    }

    const dateRegex = /(\d{1,2}\/\d{1,2}\/\d{4})|(\d{4}-\d{2}-\d{2})/;
    const dateMatch = trimmedQuery.match(dateRegex);
    let dateQuery = null;
    let nameQuery = trimmedQuery;

    if (dateMatch) {
      dateQuery = formatUsDateToYyyyMmDd(dateMatch[0]);
      nameQuery = trimmedQuery.replace(dateRegex, "").trim();
    }

    const searchablePatients = dateQuery ? patients.filter((p) => p.dateOfBirth === dateQuery) : patients;

    if (searchablePatients.length === 0) {
      return [];
    }

    if (!nameQuery && dateQuery) {
      return searchablePatients.slice(0, 20);
    }

    const exactMatches = searchablePatients.filter((p) => {
      const pGivenName = p.givenName?.toLowerCase().trim() || "";
      const pFamilyName = p.familyName?.toLowerCase().trim() || "";

      return `${pGivenName} ${pFamilyName}` === nameQuery.toLowerCase();
    });

    if (exactMatches.length > 0) {
      return exactMatches.slice(0, 20);
    }

    const fuse = new Fuse(searchablePatients, {
      keys: ["givenName", "middleName", "familyName", "displayId", "id", "dateOfBirth"],
      threshold: 0.6,
      ignoreLocation: true,
      isCaseSensitive: false,
    });

    return fuse.search(nameQuery, { limit: 20 }).map((result) => result.item);
  }

  const givenName = query.givenName?.trim().toLowerCase() || "";
  const familyName = query.familyName?.trim().toLowerCase() || "";
  const dateOfBirth = formatUsDateToYyyyMmDd(query.dateOfBirth ?? null);

  if (!givenName) {
    return [];
  }

  const searchablePatients = dateOfBirth ? patients.filter((p) => p.dateOfBirth === dateOfBirth) : patients;

  if (searchablePatients.length === 0) {
    return [];
  }

  const exactMatch = searchablePatients.find(
    (p) => p.givenName?.toLowerCase() === givenName && (!familyName || p.familyName?.toLowerCase() === familyName)
  );

  return exactMatch ? [exactMatch] : [];
}

// ---------------------------------------------------------------------------
// Patient identification
// ---------------------------------------------------------------------------

export { PatientMatchMethod };

export type PatientMatch = {
  patient: Patient;
  matchMethod: PatientMatchMethod;
};

/**
 * From a set of candidate matches, drop any that conflict on identifying fields,
 * then return the oldest survivor (the canonical record). Returns null when the set is
 * empty or every candidate conflicts.
 */
function pickBestMatch(candidates: Patient[], extracted: Patient): Patient | null {
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  const nonConflicting = candidates.filter((candidate) => !hasConflictingIdentifyingFields(extracted, candidate));

  if (nonConflicting.length === 0) return null;

  // Deterministic canonical pick: the oldest record (earliest createdAt).
  return [...nonConflicting].sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""))[0];
}

export async function identifyPatientInValerie(patient: Patient, group: Group): Promise<PatientMatch | null> {
  const start = performance.now();

  const match = await findPatientMatch(patient, group);

  stats.histogram("identifyPatientInValerie.duration", performance.now() - start, {
    tags: { groupId: group.id, matchMethod: match?.matchMethod ?? "none" },
    unit: "ms",
  });

  return match;
}

async function findPatientMatch(patient: Patient, group: Group): Promise<PatientMatch | null> {
  const dob = patient.dateOfBirth;
  const phone = patient.mobilePhoneNumber || patient.homePhoneNumber;

  const [phoneMatches, nameDobMatches] = await Promise.all([
    phone ? getPatientsByPhoneNumber({ phoneNumber: phone, groupId: group.id }) : Promise.resolve<Patient[]>([]),
    patient.givenName && patient.familyName && dob
      ? findPatients({ givenName: patient.givenName, familyName: patient.familyName, dateOfBirth: dob }, group)
      : Promise.resolve<Patient[]>([]),
  ]);

  // match by name, dob, and phone if presented
  let match = pickBestMatch(nameDobMatches, patient);

  if (match) {
    logger.info("identifyPatient: matched by name + DOB", { id: match.id });

    return { patient: match, matchMethod: PatientMatchMethod.NAME_AND_DOB };
  }

  // match by phone and name
  const phoneNameMatches = phoneMatches.filter((c) => isExactNameMatch(c, patient));

  match = pickBestMatch(phoneNameMatches, patient);
  if (match) {
    logger.info("identifyPatient: matched by phone + name", { id: match.id });

    return { patient: match, matchMethod: PatientMatchMethod.PHONE_AND_NAME };
  }

  // match by phone and dob first, then fuzzy name and dob
  if (dob) {
    // match by phone and DOB first, when several patients share a phone + DOB
    // narrow by fuzzy name match before picking the best candidate to reduce risk of misidentification (e.g., twins)
    const phoneDobMatches = phoneMatches.filter((c) => dobMatch(c, patient));
    const phoneDobCandidates =
      phoneDobMatches.length > 1
        ? matchByName(phoneDobMatches, patient, FuzzyNameMatchThreshold.Loose)
        : phoneDobMatches;

    match = pickBestMatch(phoneDobCandidates, patient);
    if (match) {
      logger.info("identifyPatient: matched by phone + DOB", { id: match.id });

      return { patient: match, matchMethod: PatientMatchMethod.PHONE_AND_DOB };
    }

    // match by fuzzy name and DOB
    const dobCandidates = await findPatients({ dateOfBirth: dob }, group);
    const fuzzyMatches = matchByName(dobCandidates, patient);

    if (fuzzyMatches.length === 1) {
      logger.info("identifyPatient: matched by DOB + fuzzy name", {
        id: fuzzyMatches[0].id,
        queryName: normalizeFullName(patient),
      });

      return { patient: fuzzyMatches[0], matchMethod: PatientMatchMethod.FUZZY_NAME_AND_DOB };
    } else if (fuzzyMatches.length > 1) {
      const candidateIds = fuzzyMatches.map((p) => p.id);

      logger.warn("identifyPatient: ambiguous — multiple fuzzy name + DOB matches", {
        patientIds: candidateIds,
        queryName: normalizeFullName(patient),
      });
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Patient persistence
// ---------------------------------------------------------------------------

export async function createOrUpdatePatient(
  patient: Patient,
  overwriteWithNull: boolean = false,
  group: Group
): Promise<Patient> {
  if (!group) {
    throw new Error("Group not provided for patient");
  }

  const existingPatient = patient.id ? await getPatient(patient.id, group) : null;

  if (existingPatient) {
    const merged = mergeNonNull(
      stripPersonDemographicPlaceholders(existingPatient),
      stripPersonDemographicPlaceholders(patient),
      overwriteWithNull
    );

    merged.createdAt = existingPatient.createdAt;
    if (hasValue(merged.ehrId)) merged.notFoundInEhr = false;

    return updatePatient(existingPatient.id, await handlePatientRelations(merged, group), group);
  }

  const patientMatch = await identifyPatientInValerie(patient, group);
  const identifiedPatient = patientMatch?.patient ?? null;

  if (identifiedPatient) {
    logger.info("createOrUpdatePatient: found patient via identification, merging and updating", {
      patientId: identifiedPatient.id,
      matchMethod: patientMatch?.matchMethod,
    });

    // Conservative merge: existing DB data takes precedence over incoming for all non-null fields.
    // Incoming data only fills gaps (nulls) in the existing record — we don't trust the incoming
    // source to be more authoritative than what Valerie already has. Identifiers are resolved
    // separately (below) so they land in the group's column convention.
    const mergedPatient = mergeNonNull(
      stripPersonDemographicPlaceholders(patient),
      stripPersonDemographicPlaceholders(identifiedPatient),
      false
    );

    const identifiers = mergeEhrIdentifiers(patient, identifiedPatient, group);

    if (identifiers.conflict) {
      logger.error(
        "createOrUpdatePatient: identifier conflict on identified patient — skipping update, manual review required",
        {
          patientId: identifiedPatient.id,
          existingEhrId: identifiers.existingEhrId,
          incomingEhrId: identifiers.incomingEhrId,
          existingMrn: identifiers.existingMrn,
          incomingMrn: identifiers.incomingMrn,
        }
      );

      return identifiedPatient;
    }

    mergedPatient.ehrId = identifiers.ehrId;
    mergedPatient.secondaryEhrId = identifiers.secondaryEhrId;
    mergedPatient.createdAt = identifiedPatient.createdAt;
    if (hasValue(identifiers.ehrId) || hasValue(identifiers.secondaryEhrId)) mergedPatient.notFoundInEhr = false;

    return updatePatient(identifiedPatient.id, await handlePatientRelations(mergedPatient, group), group);
  }

  patient.id = uuidv4();
  const now = new Date().toISOString();

  patient.createdAt = now;
  patient.updatedAt = now;
  patient.groupId = group.id;

  const finalPatient = await handlePatientRelations(patient, group);

  return insertPatient(finalPatient, group);
}

// ---------------------------------------------------------------------------
// Patient enrichment
// ---------------------------------------------------------------------------

export async function refreshPatientDetails(
  existingPatient: Patient,
  sourcePatient: Pick<Patient, "mobilePhoneNumber" | "homePhoneNumber" | "personalEmail" | "notes">,
  group: Group
): Promise<Patient | null> {
  const updates: Partial<Patient> = {};

  if (existingPatient.groupId !== group.id) {
    logAndThrow(
      "Patient group ID does not match group ID",
      { existingPatientId: existingPatient.id, groupId: group.id },
      true
    );
  }

  if (!existingPatient.mobilePhoneNumber && sourcePatient.mobilePhoneNumber) {
    updates.mobilePhoneNumber = sourcePatient.mobilePhoneNumber;
  }

  if (!existingPatient.homePhoneNumber && sourcePatient.homePhoneNumber) {
    updates.homePhoneNumber = sourcePatient.homePhoneNumber;
  }

  if (!existingPatient.personalEmail && sourcePatient.personalEmail) {
    updates.personalEmail = sourcePatient.personalEmail;
  }

  if (sourcePatient.notes) {
    updates.notes = sourcePatient.notes;
  }

  if (Object.keys(updates).length === 0) {
    return null;
  }

  logger.info("refreshPatientDetails: filling in missing patient fields", {
    patientId: existingPatient.id,
    groupId: group.id,
    fields: Object.keys(updates),
  });

  return updatePatient(existingPatient.id, updates, group);
}

/**
 * Insert a new patient (and optional coverage) into Valerie. Does not touch the EHR and does not
 * dedup — callers must run their own duplicate check before calling this.
 */
export async function insertPatientWithCoverage(
  patient: Patient,
  coverage: Coverage | null,
  group: Group
): Promise<Patient> {
  if (!patient.givenName || !patient.familyName || !patient.dateOfBirth) {
    throw new Error("insertPatientWithCoverage: patient is missing required identity fields");
  }

  const insertedPatient = await insertPatient(patient, group);

  if (coverage) {
    await insertCoverage(coverage, group);
  }

  return insertedPatient;
}

export async function upsertPatientDetails(patient: Patient, details: PatientDetails, group: Group): Promise<void> {
  await Promise.all([
    ...(details.appointments ?? []).map((a) =>
      createOrUpdateAppointmentFromEhr({ ...a, patientId: patient.id }, group)
    ),
    (details.alerts ?? []).length > 0
      ? patientAlertRepository.bulkInsert(
          (details.alerts ?? []).map((a) => ({ ...a, patientId: patient.id, groupId: group.id })),
          group,
          { skipDuplicates: true }
        )
      : Promise.resolve(),
  ]);
}

export const updatePatientIdentifiers = withDurationMetric(async function updatePatientIdentifiers(
  patientId: string,
  ehrSystemId: string | null,
  group: Group,
  mrn?: string | null,
  createdInEhr?: boolean | null
): Promise<Patient> {
  if (!hasValue(ehrSystemId) && !hasValue(mrn)) {
    logAndThrow("updatePatientIdentifiers requires at least one of ehrSystemId or mrn", {
      patientId,
      groupId: group.id,
    });
  }

  const normalized = normalizeIdentifiers(group, ehrSystemId, mrn);
  const ehrIdColumn = normalized.ehrId?.trim();
  const secondaryEhrIdColumn = normalized.secondaryEhrId?.trim();

  logger.info("updatePatientIdentifiers: writing patient row", {
    patientId,
    groupId: group.id,
    ehrSystemId,
    hasMrn: hasValue(mrn),
    willUpdateEhrIdColumn: hasValue(ehrIdColumn),
    willUpdateSecondaryEhrIdColumn: hasValue(secondaryEhrIdColumn),
  });

  return updatePatient(
    patientId,
    {
      ...(hasValue(ehrIdColumn) ? { ehrId: ehrIdColumn } : {}),
      notFoundInEhr: false,
      status: PatientStatus.Active,
      ...(hasValue(secondaryEhrIdColumn) ? { secondaryEhrId: secondaryEhrIdColumn } : {}),
      ...(hasValue(createdInEhr) ? { createdInEhr } : {}),
    },
    group
  );
}, "updatePatientIdentifiers");
